// components/GenericImageInput.tsx
import React, { useState, useRef, useCallback, useMemo, useImperativeHandle, forwardRef, useEffect } from 'react';
import { ImagePlus, CircleX, Pin } from '@/components/ui/lucide-memo';
import { getGenericImageInputContextMenuActions, ContextMenuActionDependencies } from './imageInputContextMenuUtils';
import { LockableFile } from '#types/types';
import { useKernel } from '@/kernel/Kernel';
import { getProjectSelectedLayersId } from '#lib/project-runtime-layer-ui-state';
import { imageDataToFile } from '#lib/utils';

const MemoizedCircleX = React.memo(CircleX);
const MemoizedImagePlus = React.memo(ImagePlus);
const MemoizedPin = React.memo(Pin);

type DisplayMode = 'full' | 'previewsOnly';

const LOCK_BUTTON_STYLE: React.CSSProperties = { backdropFilter: 'blur(2px)' };
const IMAGE_SRC_STYLE: React.CSSProperties = { objectFit: 'contain' };
const PREVIEW_MAX_HEIGHT_PX = 64; // Corresponds to h-16

interface ImagePreviewProps {
  url: string;
  index: number;
  lockableFile: LockableFile;
  dimension: { width: number; height: number } | undefined;
  cursor: string;
  showLock: boolean;
  registerRef: (id: string, element: HTMLDivElement | null) => void;
  onPreviewClick: (imageUrl: string, index: number, id: string) => void;
  onDelete: (index: number, event: React.MouseEvent<HTMLButtonElement>) => void;
  onToggleLock: (id: string, event: React.MouseEvent<HTMLButtonElement>) => void;
}

const ImagePreview = React.memo(({
  url,
  index,
  lockableFile,
  dimension,
  cursor,
  showLock,
  registerRef,
  onPreviewClick,
  onDelete,
  onToggleLock,
}: ImagePreviewProps) => {
  const setRef = useCallback((element: HTMLDivElement | null) => {
    registerRef(lockableFile.id, element);
  }, [registerRef, lockableFile.id]);

  const handleClick = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
    onPreviewClick(url, index, lockableFile.id);
  }, [onPreviewClick, url, index, lockableFile.id]);

  const handleDelete = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    onDelete(index, event);
  }, [onDelete, index]);

  const handleToggleLock = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    onToggleLock(lockableFile.id, event);
  }, [onToggleLock, lockableFile.id]);

  const style = useMemo<React.CSSProperties>(() => {
    let widthPx = PREVIEW_MAX_HEIGHT_PX; // Default to square 1:1

    if (dimension && dimension.height > 0) {
      const aspectRatio = dimension.width / dimension.height;
      widthPx = PREVIEW_MAX_HEIGHT_PX * aspectRatio;
      const maxWidthPx = PREVIEW_MAX_HEIGHT_PX * (16 / 9);
      if (widthPx > maxWidthPx) {
        widthPx = maxWidthPx;
      }
    }

    return { height: `${PREVIEW_MAX_HEIGHT_PX}px`, width: `${widthPx}px`, cursor };
  }, [dimension, cursor]);

  return (
    <div ref={setRef} className="relative group m-1" style={style} onClick={handleClick}>
      <img src={url} alt={`Uploaded ${index + 1}`} className="w-full h-full object-contain rounded" />
      <button
        onClick={handleDelete}
        type="button"
        className="bg-red-400 absolute -right-1 -top-1 p-0.5 flex items-center justify-center rounded-full fill-white text-xs text-white hover:bg-red-500 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
      >
        <MemoizedCircleX color="black" strokeWidth={1.5} size={16} />
      </button>
      {showLock && (
        <button
          onClick={handleToggleLock}
          type="button"
          className={`absolute -left-1 -top-1 p-0.5 flex items-center justify-center rounded-full bg-neutral-700/80 text-xs text-white hover:bg-neutral-600 transition-opacity duration-200 ${
            lockableFile.locked ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
          }`}
          style={LOCK_BUTTON_STYLE}
        >
          <MemoizedPin color="white" fill={lockableFile.locked ? "white" : "none"} size={16} strokeWidth={1.5} />
        </button>
      )}
    </div>
  );
});
ImagePreview.displayName = 'ImagePreview';

interface GenericImageInputProps {
  projectId?: string;
  label?: string;
  imageSrc?: string | null;
  description?: string;
  containerStyle?: React.CSSProperties;
  inputStyle?: React.CSSProperties;
  multiple?: boolean;
  imageFiles?: LockableFile[];
  onImagesChange: (files: LockableFile[]) => void;
  onImageClick?: (imageUrl: string, index: number, sourceRect: DOMRect | null) => void;
  minHeight?: string;
  displayMode?: DisplayMode;
  previewCursor?: string;
  showLock?: boolean;
}

export interface GenericImageInputHandle {
  triggerFileInput: () => void;
}

const GenericImageInput = React.memo(forwardRef<GenericImageInputHandle, GenericImageInputProps>(({
  projectId,
  label,
  imageSrc,
  description,
  containerStyle,
  inputStyle,
  multiple = false,
  imageFiles,
  onImagesChange,
  onImageClick,
  minHeight: minHeightProp,
  displayMode = 'full',
  previewCursor,
  showLock = true
}, ref) => {
  const { kernel } = useKernel();
  const imageInputRef = useRef<HTMLInputElement>(null);
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  // Keyed by LockableFile id, and written only by the ref lifecycle: an effect-driven
  // reset would wipe entries that the (now stable) ref callbacks never re-register.
  const previewRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  const [dimensions, setDimensions] = useState<Record<number, {width: number, height: number}>>({});

  useEffect(() => {
    if (!imageFiles || imageFiles.length === 0) {
      setDimensions({});
      return;
    }

    const newDimensions: Record<number, {width: number, height: number}> = {};
    const promises = imageFiles.map((lockableFile, index) => {
      return new Promise<void>((resolve) => {
        const img = new Image();
        const url = URL.createObjectURL(lockableFile.file);
        img.onload = () => {
          newDimensions[index] = { width: img.naturalWidth, height: img.naturalHeight };
          URL.revokeObjectURL(url);
          resolve();
        };
        img.onerror = () => {
          // Could set a default 1:1 aspect ratio on error
          newDimensions[index] = { width: 1, height: 1 };
          URL.revokeObjectURL(url);
          resolve();
        };
        img.src = url;
      });
    });

    Promise.all(promises).then(() => {
      setDimensions(newDimensions);
    });
  }, [imageFiles]);

  const currentPreviewUrls = useMemo(() => {
    return imageFiles ? imageFiles.map(lf => URL.createObjectURL(lf.file)) : [];
  }, [imageFiles]);

  useEffect(() => {
    const urlsToRevoke = currentPreviewUrls;
    return () => {
      urlsToRevoke.forEach(url => URL.revokeObjectURL(url));
    };
  }, [currentPreviewUrls]);

  const handleInputChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && onImagesChange) {
      const fileArray = Array.from(files);
      // This function now receives raw Files and must convert them to LockableFile
      // This is typically for user-initiated file selections, which should be unlocked by default.
      const { v4: uuidv4 } = await import('uuid');
      const lockableFiles: LockableFile[] = fileArray.map(file => ({
        id: uuidv4(),
        file,
        locked: false,
      }));
      if (multiple) {
        onImagesChange([...(imageFiles || []), ...lockableFiles]);
      } else {
        const firstFile = lockableFiles[0];
        onImagesChange(firstFile ? [firstFile] : []);
      }
    }
    // Important: reset the native input value so selecting the same file again
    // still fires a new change event.
    e.target.value = '';
  }, [onImagesChange, multiple, imageFiles]);

  const internalHandleClick = useCallback(() => {
    if (!imageInputRef.current) return;
    // Clear any previously selected path before opening the picker.
    imageInputRef.current.value = '';
    imageInputRef.current.click();
  }, [imageInputRef]);

  useImperativeHandle(ref, () => ({
    triggerFileInput: () => {
      internalHandleClick();
    }
  }));

  const registerPreviewRef = useCallback((id: string, element: HTMLDivElement | null) => {
    if (element) previewRefs.current.set(id, element);
    else previewRefs.current.delete(id);
  }, []);

  const handleImageClick = useCallback((imageUrl: string, index: number, id: string) => {
    if (onImageClick) {
      const sourceRect = previewRefs.current.get(id)?.getBoundingClientRect() ?? null;
      onImageClick(imageUrl, index, sourceRect);
    }
  }, [onImageClick]);

  const handleDeleteImage = useCallback((index: number, event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    if (onImagesChange && imageFiles) {
      const newFiles = imageFiles.filter((_, i) => i !== index);
      onImagesChange(newFiles);
    }
  }, [onImagesChange, imageFiles]);

  const handleToggleLock = useCallback((id: string, event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    kernel.current?.toggleReferenceImageLock(id);
  }, [kernel]);

  const handleDragOver = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    if (displayMode === 'previewsOnly') return;
    event.preventDefault();
    setIsDraggingOver(true);
  }, [displayMode, setIsDraggingOver]);

  const handleDragLeave = useCallback(() => {
    if (displayMode === 'previewsOnly') return;
    setIsDraggingOver(false);
  }, [displayMode, setIsDraggingOver]);

  const handleDrop = useCallback(async (event: React.DragEvent<HTMLDivElement>) => {
    if (displayMode === 'previewsOnly') return;
    event.preventDefault();
    event.stopPropagation()
    setIsDraggingOver(false);
    const layerIdString = event.dataTransfer.getData("text/plain");
    const droppedFiles = event.dataTransfer.files;

    if (layerIdString && kernel.current) {
      const layerId = layerIdString;
      const droppedLayer = kernel.current.getLayerRegistry()[layerId];
      if (droppedLayer && droppedLayer.imageData) {
        const file = await imageDataToFile(droppedLayer.imageData, 'layer-image.webp', 'image/webp');
        if (file) {
          const { v4: uuidv4 } = await import('uuid');
          const lockableFile: LockableFile = { id: uuidv4(), file, locked: false };
          onImagesChange(multiple ? [...(imageFiles || []), lockableFile] : [lockableFile]);
        }
        return;
      }
    }

    if (droppedFiles && droppedFiles.length > 0 && onImagesChange) {
      const newFilesArray = Array.from(droppedFiles);
      const { v4: uuidv4 } = await import('uuid');
      const lockableFiles: LockableFile[] = newFilesArray.map(file => ({
        id: uuidv4(),
        file,
        locked: false,
      }));

      if (multiple) {
        onImagesChange([...(imageFiles || []), ...lockableFiles]);
      } else {
        const firstFile = lockableFiles[0];
        if (firstFile) {
          onImagesChange([firstFile]);
        }
      }
    }
  }, [displayMode, setIsDraggingOver, onImagesChange, imageFiles, multiple, kernel]);

  const handleContextMenu = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (displayMode === 'previewsOnly' && (!imageFiles || imageFiles.length === 0) || !kernel.current) {
      return; // No context menu on empty, collapsed preview area
    }

    const normalizedProjectId = typeof projectId === 'string' ? projectId.trim() : '';
    if (!normalizedProjectId) return;

    const currentLayers = kernel.current.listLayersByProjectId(normalizedProjectId);
    const workspaceId = kernel.current.getProjectUIWorkspaceId(normalizedProjectId);

    const deps: ContextMenuActionDependencies = {
      workspaceId,
      onImagesChange,
      imageFiles,
      imageSrc,
      layers : currentLayers,
      selectedLayersId: normalizedProjectId ? getProjectSelectedLayersId(kernel.current, normalizedProjectId) : [],
      multiple,
      kernel: kernel,
      projectType: normalizedProjectId
        ? (kernel.current.getInventory().Project.registry[normalizedProjectId] as { projectType?: string } | undefined)?.projectType
        : undefined,
    };
    const actions = getGenericImageInputContextMenuActions(deps);

    if (actions.length > 0) {
      kernel.current?.setMenuData({
        position: { x: event.clientX, y: event.clientY },
        actions: actions,
      });
    } else {
      kernel.current?.setMenuData(null); // Close if no actions
    }
  }, [
    displayMode, imageFiles, imageSrc, kernel, multiple, projectId,
    onImagesChange
  ]);

  const mainDivHandleClick = useCallback(() => {
    if (displayMode === 'previewsOnly') return;
    internalHandleClick();
  }, [displayMode, internalHandleClick]);

  const hasImages = imageFiles && imageFiles.length > 0;
  const isEffectivelyCollapsedForPlaceholder = !hasImages && (minHeightProp === '0px' || minHeightProp === '0');

  const divStyle = useMemo<React.CSSProperties>(() => {
    const divStyle: React.CSSProperties = {
      display: 'flex',
      transition: 'background-color 0.2s, border-color 0.2s, min-height 0.2s, height 0.2s, padding 0.2s, margin 0.2s',
      ...inputStyle,
    };

    if (displayMode === 'full') {
      // ... (styling for 'full' mode remains largely the same)
      divStyle.cursor = 'pointer';
      divStyle.borderRadius = divStyle.borderRadius ?? '20px';
      divStyle.minHeight = minHeightProp || (hasImages ? 'auto' : '100px');

      if (hasImages) {
        divStyle.padding = divStyle.padding ?? '8px';
        divStyle.border = divStyle.border ?? '1px dashed #202020';
        divStyle.alignItems = 'flex-start';
        divStyle.justifyContent = 'flex-start';
        divStyle.flexWrap = 'wrap';
      } else {
        if (isEffectivelyCollapsedForPlaceholder && !isDraggingOver) {
          divStyle.padding = '0px';
          divStyle.border = 'none';
          divStyle.minHeight = '0px'; // Ensure collapse
          divStyle.height = '0px';    // Ensure collapse
          divStyle.overflow = 'hidden'; // Ensure collapse
        } else {
          divStyle.padding = divStyle.padding ?? '4px';
          divStyle.border = divStyle.border ?? '1px dashed #202020';
          divStyle.alignItems = 'center';
          divStyle.justifyContent = 'center';
        }
      }
      if (isDraggingOver) {
        divStyle.backgroundColor = '#404040';
        divStyle.border = `2px dashed #303030`;
        if (isEffectivelyCollapsedForPlaceholder) {
          divStyle.minHeight = '100px'; // Temporary expand for drop
          divStyle.height = 'auto';
          divStyle.padding = '4px';
          divStyle.overflow = 'visible';
        }
      }
    } else { // displayMode === 'previewsOnly'
      divStyle.flexWrap = 'wrap';
      divStyle.alignItems = 'flex-start';
      divStyle.minHeight = minHeightProp || (hasImages ? 'auto' : '0px'); // Use minHeightProp or collapse

      if (!hasImages) {
        divStyle.padding = '0px';
        divStyle.margin = '0px';
        divStyle.height = '0px';    // Explicitly set height to 0
        divStyle.overflow = 'hidden'; // Hide any potential residual content/border
        divStyle.border = 'none'; // Ensure no border contributes to height
      } else {
        divStyle.paddingTop = '0px'; // Default padding for previews
        divStyle.paddingBottom = '0px'; // Default padding for previews
        divStyle.paddingLeft = '8px'; // Default padding for previews
        divStyle.paddingRight = '8px'; // Default padding for previews
        divStyle.height = 'auto';
        divStyle.overflow = 'visible';
      }
    }

    return divStyle;
  }, [inputStyle, displayMode, hasImages, minHeightProp, isDraggingOver, isEffectivelyCollapsedForPlaceholder]);

  return (
    <div style={containerStyle}>
      {label && displayMode === 'full' && <label className="block text-sm font-medium text-white mb-1">{label}</label>}
      <div
        className={displayMode === 'full' ? `classic` : ''}
        onClick={mainDivHandleClick}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onDragLeave={handleDragLeave}
        onContextMenu={handleContextMenu} // Context menu handled here
        style={divStyle}
      >
        {hasImages ? (
          currentPreviewUrls.map((url, index) => {
            const lockableFile = imageFiles![index]!;
            return (
              <ImagePreview
                key={lockableFile.id}
                url={url}
                index={index}
                lockableFile={lockableFile}
                dimension={dimensions[index]}
                cursor={previewCursor || 'pointer'}
                showLock={showLock}
                registerRef={registerPreviewRef}
                onPreviewClick={handleImageClick}
                onDelete={handleDeleteImage}
                onToggleLock={handleToggleLock}
              />
            );
          })
        ) : imageSrc && displayMode === 'full' && !isEffectivelyCollapsedForPlaceholder ? (
          <img src={imageSrc} alt="Uploaded" className="max-w-full max-h-full rounded" style={IMAGE_SRC_STYLE} />
        ) : displayMode === 'full' && (!isEffectivelyCollapsedForPlaceholder || (isDraggingOver && isEffectivelyCollapsedForPlaceholder)) && (!imageSrc) ? (
          <MemoizedImagePlus size={16} className="text-white opacity-40" />
        ) : null}
      </div>
      <input
        ref={imageInputRef}
        type="file"
        accept="image/*"
        onChange={handleInputChange}
        className="hidden"
        multiple={multiple}
      />
      {description && displayMode === 'full' && <p className="text-xs text-[#4D4D4D] mt-1">{description}</p>}
    </div>
  );
}));
GenericImageInput.displayName = 'GenericImageInput';

export default GenericImageInput;

